import React from 'react';

import { useDebouncedCallback, useResizeObserver } from '../hooks';
import { isDefined } from '../is';

interface Options {
  widthOnly?: boolean;
  heightOnly?: boolean;
  scrollHeightAdjustor?: number;
}

/**
 * Determines whether the given element is overflowing in terms of width or height.
 * @param ref - The ref object of the element to check for overflow.
 * @param options - Optional configuration options.
 * @param options.widthOnly - If set to true, only checks for width overflow. Default is false.
 * @param options.heightOnly - If set to true, only checks for height overflow. Default is false.
 * @param options.scrollHeightAdjustor - A number to adjust the scroll height by. Default is 0.
 * @param dependencies - An array of dependencies to trigger an update of the overflowing state.
 * @returns A boolean value indicating whether the element is overflowing.
 */
export const useIsOverflowing = (ref: React.RefObject<HTMLElement>, options?: Options, dependencies: unknown[] = []) => {
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const updateOverflowingState = React.useCallback(() => {
    if (!isDefined(ref.current)) {
      return;
    }

    const isWidthOverflowing = !options?.heightOnly && ref.current.scrollWidth > ref.current.offsetWidth;
    const isHeightOverflowing =
      !options?.widthOnly && ref.current.scrollHeight - (options?.scrollHeightAdjustor ?? 0) > ref.current.offsetHeight;

    setIsOverflowing(isWidthOverflowing || isHeightOverflowing);
  }, [options]);

  const updateOverflowingStateDebounced = useDebouncedCallback(updateOverflowingState);

  useResizeObserver(ref, updateOverflowingStateDebounced, dependencies);

  return isOverflowing;
};
